import {Injectable} from '@angular/core';
import {GridLayoutService} from '@irb-common/interfaces/grid-layout.service';
import {DeleteHeaderComponent} from '@irb-common/components/grid/renderers/delete.header.component';
import {ComRestrictionsComponent} from '@irb/irb-editors/editor-portfolio/com-restrictions/com-restrictions.component';
import {UtilityService} from '@irb-common/services/utility.service';
import {DropdownFilterRendererComponent} from '@irb-common/components/grid/renderers/dropdown-filter.cell.component';
import {DropdownSearchRendererComponent} from '@irb-common/components/grid/renderers/dropdown-search.cell.component';
import {CallToActionCellComponent} from '@irb-common/components/grid/renderers/calltoaction.cell.component';
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import { TruncateTooltipCellComponent } from '@irb/irb-common/components/grid/renderers/truncatetooltip.cell.component';
import {IrbModelsEvents} from '@irb/irb-models/irb-models.events';

@Injectable({
    providedIn: 'root'
})
export class ComRestrictionsGridLayoutService implements GridLayoutService<ComRestrictionsComponent> {
    altSecurityName: any;
    typeRestriction: string[] = [];

    constructor(private util: UtilityService) {
    }

    getFrameworkComponents() {
        return {
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
            "DropdownSearchRendererComponent": DropdownSearchRendererComponent,
            "CallToActionCellComponent": CallToActionCellComponent,
            "TruncateTooltipCellComponent": TruncateTooltipCellComponent
        };
    }

    displayValue(params: any) {
        if (params.data.securityMasterId != null) {
            return params.data.securitySymbol;
        } else if (params.data.categoryId != null) {
            return params.data.categoryLabel;
        } else {
            return "";
        }
    }

    displayDescription(params: any) {
        if (params.data.securityMasterId != null) {
            return params.data.securityDescription;
        } else if (params.data.categoryId != null) {
            return params.data.categoryDesc;
        } else {
            return "";
        }
    }

    canEdit(params: any) {
        if (params.data.restriction === "Never Buy" ||
            params.data.restriction === "Never Buy and Never Sell") {
            return true;
        } else {
            return false;
        }
    }

    canEditAltSec(params: any) {
      return params.data.condition === "Buy an alternate security";
    }

    getActionValue(params: any) {
        if (params.data.restriction === "Never Buy" || params.data.restriction === "Never Buy and Never Sell") {
            return params.data.condition;
        } else {
            return 'n/a';
        }
    }

    getAltSecValue(params: any) {
        if ((params.data.restriction === "Never Buy" ||
            params.data.restriction === "Never Buy and Never Sell") &&
            params.data.condition === 'Buy an alternate security') {
            return params.data.altSecuritySymbol;
        } else {
            return 'n/a';
        }
    }


    getColumnDefs(parent: ComRestrictionsComponent) {
        return [{
            headerName: 'Delete All',
            headerComponent: 'DeleteHeaderComponent',
            headerComponentParams: {
                message: "Remove ALL portfolio-specific restrictions?",
                proceed: parent.deleteAll.bind(parent),
                cancel: parent.cancelDelete.bind(parent)
            },
            cellRenderer: 'CallToActionCellComponent',
            cellRendererParams: {
                getTemplate: (params: any) => parent.getTemplate(params),
                calltoactionPosition: "bottom",
                icon: "delete"
            },
            resizable: false,
            minWidth: 25,
            maxWidth: 25,
            width: 25
        },
            {
                headerName: 'Symbol/Category',
                field: 'securitySymbol',
                valueGetter: this.displayValue,
                sort: "asc",
                resizable: false,
                comparator: this.util.sortString.bind(parent),
                suppressMovable: true,
                flex: 1
            },
            {
                headerName: 'Description',
                field: 'securityDescription',
                valueGetter: this.displayDescription,
                resizable: false,
                comparator: this.util.sortString.bind(parent),
                cellRenderer: "TruncateTooltipCellComponent",
                flex: 2
            },
            {
                headerName: 'Type',
                field: 'restriction',
                headerComponentParams: {
                    irbColInfo: this.RestrictionTypeToolTipColInfo()
                },
                valueSetter: function (params: any) {
                    params.data.restriction = params.newValue;
                    if (params.oldValue !== params.newValue) {
                        params.data.altSecuritySymbol = null;
                        params.data.altSecurityId = null;
                        params.data.altSecurityDesc = null;
                        params.data.condition = null;
                        params.data.securitiesDropDown = null;
                    }
                    parent.validateInput();
                    return true;
                }.bind(parent),
                resizable: false,
                cellClass: 'dropdownFilterCell',
                cellRenderer: 'DropdownFilterRendererComponent',
                cellRendererParams: (params: any) => {
                    return {
                        defaultValue: {name: params.data.restriction, value: params.data.restriction},
                        values: [
                            {name: 'No Restriction', value: 'No Restriction'},
                            {name: 'Never Sell', value: 'Never Sell'},
                            {name: 'Exclude', value: 'Exclude'},
                            {name: 'Never Buy', value: 'Never Buy'},
                            {name: 'Never Buy and Never Sell', value: 'Never Buy and Never Sell'}
                        ],
                        onChange: (params: any, item: IDropdownItem) => {
                            params.data.restriction = item.value;

                            if (params.data.restriction === 'Never Buy' || params.data.restriction === 'Never Buy and Never Sell') {
                                params.data.condition = 'Leave in cash';   
                                params.api.redrawRows({rowNodes: [params.node]});
                                const rowIndex = params.rowIndex || params.node.rowIndex;
                                params.api.setFocusedCell(rowIndex,'condition');
                            }else{
                                params.data.condition = null;  
                                params.api.redrawRows({rowNodes: [params.node]});
                            }
                            parent.validateInput();

                            
                    }
                    };
                },
                flex: 1
            },
            {
                headerName: 'Action to Take',
                field: 'condition',
                valueSetter: function (params: any) {
                    params.data.condition = params.newValue;
                    if (params.oldValue !== params.newValue) {
                        params.data.altSecuritySymbol = null;
                        params.data.altSecurityId = null;
                        params.data.altSecurityDesc = null;
                        params.data.securitiesDropDown = null;
                    }
                    parent.validateInput();
                    return true;
                }.bind(parent),
                resizable: false,
                cellClass: 'dropdownFilterCell',
                cellRenderer: 'DropdownFilterRendererComponent',
                cellRendererParams: (params: any) => {
                    return {
                        defaultValue: {name: params.data.condition, value: params.data.condition},
                        values: [
                            {name: 'Leave in cash', value: 'Leave in cash'},
                            {name: 'Buy an alternate security', value: 'Buy an alternate security'}, 
                            {name: 'Pro-rate across other model securities', value: 'Pro-rate across other model securities'}
                        ],
                        isDisabled: !this.canEdit(params),
                        isNotApplicable: !(params.data.restriction === "Never Buy" || params.data.restriction === "Never Buy and Never Sell"),
                        onChange: (params: any, item: IDropdownItem) => {
                                params.data.condition = item.value;

                                if (params.data.condition === 'Buy an alternate security') {
                                    params.api.redrawRows({rowNodes: [params.node]});
                                    params.data.altSecurityId = null;
                                    const rowIndex = params.rowIndex || params.node.rowIndex;
                                    params.api.setFocusedCell(rowIndex,'altSecurityId');
                                }else {
                                    params.data.altSecurityId = null;
                                    params.api.redrawRows({rowNodes: [params.node]});
                                }

                                parent.validateInput();
                        }
                    };
                },
                flex: 1
            },
            {
                headerName: 'Alternate Security',
                field: 'altSecurityId',
                resizable: false,
                cellClass: 'dropdownSearchCell',
                cellRenderer: 'DropdownSearchRendererComponent',
                cellRendererParams: (params: any) => {
                    const addNewItemConfig = {
                        button: 'Add new security',
                        type: 'security',
                        fromComp: 'ComRestrictionsComponentGrid',
                        rowIndex: params.rowIndex || params.node.rowIndex
                    };
                    return {
                        defaultValue: {name: params.data.altSecuritySymbol, value: params.data.altSecurityId},
                        values: (parent.securitiesDropDown) ? parent.securitiesDropDown : [],
                        disabled: () => !this.canEditAltSec(params),
                        isNotApplicable: params.data.condition !== "Buy an alternate security",
                        addNewItemConfig: addNewItemConfig,
                        hoverPanelWidth: 175,
                        onChange: (params: any, item: IDropdownItem) => {
                                params.data.altSecuritySymbol = item.name;
                                params.data.altSecurityId = item.value;
                                params.api.redrawRows({rowNodes: [params.node]});
                                parent.validateInput();
                        },
                        openNewItemFlow: (dataParam: any) => {
                            dataParam = Object.assign({}, addNewItemConfig, dataParam);
                            parent.openNewItemSecurityFlow(dataParam, true);
                        }
                    };
                },
                flex: 1
            }];
    }
    RestrictionTypeToolTipColInfo(){
        return `
          <div>
                    <p><strong>No restriction</strong> - this setting will override any restriction set in the security master or model for this portfolio.</p>
                    <p><strong>Never sell</strong> - holdings of this security will be counted toward the portfolio's allocation, but will never be sold.</p>
                    <p><strong>Exclude</strong> - holdings of security will not be counted toward the portfolio's allocation, and will not be sold.</p>
                    <p><strong>Never buy</strong> - If the security is in the portfolio's model or listed as an alternate, iRebal will not create a buy when this selection is made. If you select never buy, you must select the action iRebal should take instead of buying the security.</p>
                    <p><strong>Never buy and never sell</strong> - If the security is in the portfolio model or listed as an alternate, iRebal will not create a buy when this selection is made. This setting will be counted toward the portfolio's allocation but will never be sold. You must select the action iRebal should take instead of buying the security.</p>
                    
                </div>
                
          `
    }
}
